/**
 * Telemetry reads (ADR-0038): a structured metric query against a telemetry
 * source the caller administers, at an instant or over a window. The query
 * is data, never an expression — a metric name, equality matchers, an
 * optional rate/increase and an optional aggregation — and the UI builds
 * exactly that (master prompt §12.4: no unrestricted PromQL). Both calls
 * need `integration.manage` until service-scoped reads exist (DL-243 item 2,
 * pass 2), which is why the metrics explorer picks an integration and not a
 * service.
 */

import { expect, http } from './client';
import type { components } from './gen/schema';

type Schemas = components['schemas'];

export type TelemetryInstantQuery = Schemas['TelemetryInstantQuery'];
export type TelemetryRangeQuery = Schemas['TelemetryRangeQuery'];
export type TelemetryAnswer = Schemas['TelemetryAnswer'];
export type TelemetrySeries = Schemas['TelemetrySeries'];
export type TelemetryPoint = Schemas['TelemetryPoint'];
export type TelemetryMatcher = Schemas['TelemetryMatcher'];
export type TelemetryFunction = NonNullable<TelemetryRangeQuery['function']>;
export type TelemetryAggregation = NonNullable<TelemetryRangeQuery['aggregation']>;

/** The range query's own bounds, mirrored so the form refuses before the server does. */
export const TELEMETRY_MAX_RANGE_SECONDS = 31 * 86_400;
export const TELEMETRY_MAX_POINTS = 11_000;
export const TELEMETRY_MAX_MATCHERS = 10;

const integrationPath = (orgId: string, integrationId: string) => ({
  org_id: orgId,
  integration_id: integrationId,
});

export function queryIntegrationTelemetry(
  orgId: string,
  integrationId: string,
  body: TelemetryInstantQuery,
): Promise<TelemetryAnswer> {
  return expect(
    http.POST('/organizations/{org_id}/integrations/{integration_id}/telemetry/query', {
      params: { path: integrationPath(orgId, integrationId) },
      body,
    }),
  );
}

export function queryIntegrationTelemetryRange(
  orgId: string,
  integrationId: string,
  body: TelemetryRangeQuery,
): Promise<TelemetryAnswer> {
  return expect(
    http.POST('/organizations/{org_id}/integrations/{integration_id}/telemetry/query-range', {
      params: { path: integrationPath(orgId, integrationId) },
      body,
    }),
  );
}

/**
 * A step that keeps a window under about `target` points per series and
 * inside the contract's 11,000-point bound, in whole seconds (at least 1).
 */
export function stepFor(rangeSeconds: number, target = 240): number {
  const raw = Math.ceil(rangeSeconds / Math.max(1, Math.min(target, TELEMETRY_MAX_POINTS - 1)));
  const steps = [
    1, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600, 7200, 14_400, 21_600, 43_200, 86_400,
  ];
  return steps.find((s) => s >= raw) ?? Math.ceil(raw);
}
